/**
 * MKS-372 — the Time & Material pre-conversion surface.
 *
 * Mirrors `backend/src/api/routes/tmConversionRoutes.ts`. Every endpoint here
 * is behind the `mks_372_tm_conversion_check` release flag on the server, so
 * with the flag off they 404 — callers must gate on `useFlag` rather than
 * letting a 404 surface as an error.
 */
import { apiGet, apiPost } from './client';

export type TmReadinessIssueType =
  | 'assignment_without_role'
  | 'role_without_rate'
  | 'role_rate_conflict'
  | 'currency_mismatch';

export interface TmReadinessPerson {
  assignmentId: number;
  userId: number | null;
  userName: string | null;
  billingRate: string | null;
  billingRateCurrency: string;
}

export interface TmReadinessValue {
  billingRate: string | null;
  billingRateCurrency: string;
  people: TmReadinessPerson[];
}

export interface TmReadinessIssue {
  type: TmReadinessIssueType;
  roleId: number | null;
  roleName: string | null;
  people: TmReadinessPerson[];
  values?: TmReadinessValue[];
  projectCurrency?: string;
  rateCurrency?: string;
  message: string;
}

export interface TmConversionReadiness {
  projectId: number;
  ready: boolean;
  issues: TmReadinessIssue[];
}

export interface TmSeedRoleOption {
  roleId: number;
  roleName: string | null;
  hasRate: boolean;
  currentRate: string | null;
  currentCurrency: string | null;
  candidates: TmReadinessValue[];
}

export interface TmConversionSeedOptions {
  projectId: number;
  tmBillingCurrency: string;
  roles: TmSeedRoleOption[];
  rolelessPeople: TmReadinessPerson[];
}

export interface TmConversionSeedInput {
  roleRates?: Array<{ roleId: number; hourlyRate: string; currency?: string }>;
  assignmentRoles?: Array<{ assignmentId: number; roleId: number }>;
}

export interface TmConversionSeedResult {
  ratesWritten: number;
  assignmentsRoled: number;
}

export interface DefaultProjectReadinessRow {
  projectId: number;
  projectName: string | null;
  ready: boolean;
  issueCount: number;
  issues: TmReadinessIssue[];
}

export async function fetchTmConversionReadiness(
  projectId: number,
): Promise<TmConversionReadiness> {
  const res = await apiGet<{ data: TmConversionReadiness }>(
    `/projects/${projectId}/tm-conversion-readiness`,
  );
  return res.data;
}

export async function fetchTmConversionSeedOptions(
  projectId: number,
): Promise<TmConversionSeedOptions> {
  const res = await apiGet<{ data: TmConversionSeedOptions }>(
    `/projects/${projectId}/tm-conversion-seed`,
  );
  return res.data;
}

export async function applyTmConversionSeed(
  projectId: number,
  input: TmConversionSeedInput,
): Promise<TmConversionSeedResult> {
  const res = await apiPost<{ data: TmConversionSeedResult }>(
    `/projects/${projectId}/tm-conversion-seed`,
    input,
  );
  return res.data;
}

export async function fetchDefaultProjectReadiness(): Promise<DefaultProjectReadinessRow[]> {
  const res = await apiGet<{ data: DefaultProjectReadinessRow[] }>(
    '/admin/tm-conversion-readiness',
  );
  return res.data;
}
